import { useState } from 'react';
import { PasswordInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconKey, IconLock } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { clientRoutes } from '@/ApiPaths';
import { changePassword } from '@/Api';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import {
  AuthPageShell,
  AuthSubmitButton,
  AuthSecondaryButton,
} from '@/components/auth/AuthPageShell';
import { getAuthInputStyles } from '@/components/auth/authInputStyles';
import authClasses from '@/components/auth/AuthPage.module.css';

function ChangePasswordForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const accentColor = DEFAULT_QR_CODE_COLOR;
  const inputStyles = getAuthInputStyles(accentColor);

  const form = useForm({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: {
      currentPassword: (value) => (value.length ? null : t('changePassword.currentRequired')),
      newPassword: (value) =>
        value.length >= 6 ? null : t('resetPassword.passwordMinLength'),
      confirmPassword: (value, values) =>
        value === values.newPassword ? null : t('resetPassword.passwordMismatch'),
    },
  });

  const handleSubmit = async (values: { currentPassword: string; newPassword: string }) => {
    setIsSubmitting(true);
    try {
      await changePassword(values.currentPassword, values.newPassword);
      setSuccess(true);
      notifications.show({
        title: t('changePassword.success'),
        message: '',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      setTimeout(() => navigate(clientRoutes.account), 2000);
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        t('changePassword.error');
      notifications.show({
        title: msg,
        message: '',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageShell
      title={t('changePassword.title')}
      subtitle={t('changePassword.subtitle')}
      icon={<IconKey size={22} stroke={1.75} />}
      accentColor={accentColor}
      footer={
        <AuthSecondaryButton
          onClick={() => navigate(clientRoutes.account)}
          accentColor={accentColor}
        >
          {t('changePassword.backToAccount')}
        </AuthSecondaryButton>
      }
    >
      {success ? (
        <p className={authClasses.statusMessage}>{t('changePassword.redirectToAccount')}</p>
      ) : (
        <form onSubmit={form.onSubmit(handleSubmit)} className={authClasses.formStack}>
          <PasswordInput
            label={t('changePassword.currentPassword')}
            placeholder={t('changePassword.currentPasswordPlaceholder')}
            leftSection={<IconLock size={17} />}
            size="md"
            radius="md"
            styles={inputStyles}
            {...form.getInputProps('currentPassword')}
            required
          />
          <PasswordInput
            label={t('changePassword.newPassword')}
            placeholder={t('changePassword.newPasswordPlaceholder')}
            leftSection={<IconLock size={17} />}
            size="md"
            radius="md"
            styles={inputStyles}
            {...form.getInputProps('newPassword')}
            required
          />
          <PasswordInput
            label={t('changePassword.confirmPassword')}
            placeholder={t('changePassword.confirmPasswordPlaceholder')}
            leftSection={<IconLock size={17} />}
            size="md"
            radius="md"
            styles={inputStyles}
            {...form.getInputProps('confirmPassword')}
            required
          />
          <AuthSubmitButton loading={isSubmitting} accentColor={accentColor}>
            {t('changePassword.submit')}
          </AuthSubmitButton>
        </form>
      )}
    </AuthPageShell>
  );
}

export function ChangePassword() {
  return (
    <SessionAuth>
      <ChangePasswordForm />
    </SessionAuth>
  );
}
